import React, { FC, Fragment, PropsWithChildren } from 'react';
import {
  isEmptyArray,
  isString,
  Literal,
  object,
  RemirrorJSON,
} from '@remirror/core';
import { Callout, TextHandler } from '@remirror/react-renderer';
import { Heading } from '@remirror/react-renderer';
import { MarkMap } from '@remirror/react-renderer';
import { t } from '@lingui/macro';
import { AllStyledComponent } from '@remirror/styles/emotion';
import { ErrorBoundary } from 'utils/errors';
import { ThemeProvider } from 'components/Editor/ThemeProvider';
import { useColorModeValue } from '@cardboard-ui/react';

export const Doc: FC<SubRenderTreeProps> = ({ node, ...props }) => {
  let content = node.content;

  if (!content || isEmptyArray(content)) {
    return null;
  }

  if (content.length > 5) {
    content = content.slice(0, 5);
  }

  const children = content.map((child, ii) => {
    return <UnstyledRemirrorRenderer json={child} {...props} key={ii} />;
  });

  return <div {...(node.attrs ?? object())}>{children}</div>;
};

interface MentionAttributes {
  id?: string;
  label?: string;
  name?: string;
}

const MentionRenderer: FC<{
  node: RemirrorJSON;
  markMap: MarkMap;
}> = ({ node, markMap }) => {
  const attrs = node.attrs as MentionAttributes;
  return <span>@{attrs.label}</span>;
};

const Paragraph: FC<
  PropsWithChildren<{
    node: RemirrorJSON;
    markMap: MarkMap;
  }>
> = ({ node, children }) => {
  let attrs: Record<string, Literal> = {
    ...(node.attrs ?? object()),
    style: undefined,
  };

  if (attrs['nodeTextAlignment']) {
    attrs = {
      ...attrs,
      style: {
        'text-align': attrs['nodeTextAlignment'],
      },
      nodeTextAlignment: undefined,
    };
  }

  return <p {...attrs}>{children}</p>;
};

const defaultTypeMap: MarkMap = {
  blockquote: 'blockquote',
  bulletList: 'ul',
  callout: Callout,
  doc: Doc,
  heading: Heading,
  paragraph: Paragraph,
  horizontalRule: 'hr',
  hardBreak: 'br',
  orderedList: 'ol',
  text: TextHandler,
  listItem: 'li',
  mentionAtom: MentionRenderer,
};

const createHighlightHandler =
  () =>
  ({
    children,
    highlight,
  }: PropsWithChildren<{ highlight?: string | undefined | null }>) => {
    if (highlight) {
      return <mark style={{ backgroundColor: highlight }}>{children}</mark>;
    } else {
      return <mark>{children}</mark>;
    }
  };

const defaultMarkMap: MarkMap = {
  italic: 'em',
  bold: 'strong',
  code: 'code',
  underline: 'u',
  strike: 's',
  textHighlight: createHighlightHandler(),
};

export interface BaseRenderTreeProps {
  skipUnknownTypes: boolean;
  skipUnknownMarks: boolean;
  markMap: MarkMap;
  typeMap: MarkMap;
  children?: never;
}

export interface SubRenderTreeProps extends BaseRenderTreeProps {
  node: RemirrorJSON;
}

export interface RenderTreeProps extends Partial<BaseRenderTreeProps> {
  json: RemirrorJSON | string;
}

interface PageThumbnailRendererProps extends RenderTreeProps {
  fallback: React.ReactElement;
}

const PageThumbnailRenderer: FC<PageThumbnailRendererProps> = ({
  fallback,
  ...props
}) => {
  const textColor = useColorModeValue('black', 'white');

  return (
    <ErrorBoundary fallback={fallback}>
      <AllStyledComponent className="remirror-renderer">
        <ThemeProvider
          theme={{
            color: { text: textColor },
          }}
        >
          <UnstyledRemirrorRenderer {...props} />
        </ThemeProvider>
      </AllStyledComponent>
    </ErrorBoundary>
  );
};

/**
 * A recursively rendered tree.
 */
const UnstyledRemirrorRenderer: FC<RenderTreeProps> = (props) => {
  const {
    json: originJson,
    markMap = defaultMarkMap,
    typeMap = defaultTypeMap,
    skipUnknownMarks = true,
    skipUnknownTypes = true,
  } = props;

  let json: RemirrorJSON;
  try {
    json = typeof originJson === 'string' ? JSON.parse(originJson) : originJson;
  } catch (e) {
    return null;
  }

  if (
    json.type === 'text' &&
    json.text &&
    (!json.marks || isEmptyArray(json.marks))
  ) {
    return <Fragment>{json.text}</Fragment>; // For some reason FunctionalComponent don't allow returning react-nodes
  }

  const rest = { markMap, typeMap, skipUnknownMarks, skipUnknownTypes };
  const TypeHandler = typeMap[json.type];

  if (!TypeHandler) {
    if (!skipUnknownTypes) {
      console.log('Renderer error', json);
      //   throw new Error(`No handler for node type \`${json.type}\` registered`);
      return <div>{t`Error rendering content`}</div>;
    }

    return null;
  }

  const childProps = isString(TypeHandler)
    ? { ...(json.attrs ?? object()), style: undefined }
    : { ...rest, node: json };
  const { content } = json;

  if (!content) {
    // This is important distinction. Some TypeHandler will not allow children in their props
    //   passing children would crach the entire editor, so let us not do that.
    return <TypeHandler {...childProps} />;
  } else {
    return (
      // We can limit the amount of nodes we want to render, as we will always have a smaller window
      <TypeHandler {...childProps}>
        {content.slice(0, 10).map((child, ii) => (
          <UnstyledRemirrorRenderer key={ii} json={child} {...rest} />
        ))}
      </TypeHandler>
    );
  }
};

export default PageThumbnailRenderer;
